import classNames from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'

import { CopyWithText } from '@apps-common/components/common'
import { FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'

import { SelectInputSM } from '../form'

import { Pagination } from './Pagination'

interface IProps {
  loading?: boolean
  loadingSkeletonHeight?: string
  placeholderSkeletonHeight?: string
  pagination?: {
    loading?: boolean
    count?: number
    skip: number
    take: number
    onTakeChanged: (take: number) => void
    onSkipChanged: (skip: number) => void
  }
  keysId?: string
  keys: {
    title?: string
    key: string
    static?: boolean
    changeable?: boolean
    optional?: boolean
    noCopy?: boolean
    hidden?: boolean
    thClasses?: string
    trClasses?: string
    tdClasses?: string
    Component?: any
  }[]
  data?: any[]
  sliceData?: boolean
  topLeftComponent?: any
  topRightComponent?: any
  tbodyClasses?: string
  onSkipChanged: (skip: number) => void
  onTakeChanged: (take: number) => void
}

export const Table: React.FC<IProps> = ({
  loading,
  loadingSkeletonHeight,
  placeholderSkeletonHeight,
  pagination,
  keysId,
  keys,
  data,
  sliceData = false,
  topLeftComponent,
  topRightComponent,
  tbodyClasses,
  onSkipChanged,
  onTakeChanged
}) => {
  const [tableKeys, setTableKeys] = useState(keys.filter((tk) => !tk.optional))

  useEffect(() => {
    setTableKeys(keys.filter((tk) => !tk.optional && !tk.hidden))
  }, [keysId])

  useEffect(() => {
    if (!loading && pagination?.skip && Array.isArray(data) && data.length === 0) {
      onSkipChanged(0)
    }
  }, [data, loading, onSkipChanged, pagination?.skip])

  const modifiedData = useMemo(
    () => (sliceData ? data?.slice(pagination?.skip, (pagination?.skip || 0) + (pagination?.take || FILTER_DEFAULT_TAKE)) : data),
    [sliceData, data]
  )

  return (
    <>
      <div className='flex items-center justify-between w-full text-[0.85em] pl-[10px] mb-[20px]'>
        {topLeftComponent}
        {topRightComponent}
      </div>
      <div className='border border-border rounded-[18px]'>
        <table className='table table-auto text-left w-full'>
          <thead className='border-b-border border-b'>
            <tr className='bg-transparent'>
              {tableKeys.map((k, i) => (
                <th aria-label={`${k.key}-${i}`} className={classNames('bg-surface-2', k.thClasses)}>
                  {!k.changeable && k.title}
                  {k.changeable && (
                    <SelectInputSM
                      label={k.title || k.key}
                      containerClassName='!p-0 !m-0 flex items-center justify-start !w-auto !min-w-auto'
                      className={classNames(
                        'inline-block bg-transparent !text-text border-none !p-0 !m-0 !pr-[15px] !w-auto !min-w-auto',
                        k.thClasses
                      )}
                      iconClassName='!relative'
                      hideError
                      options={keys
                        .filter((tk) => {
                          if (tk.static || tk.hidden) return false

                          return tk.key === k.key || !tableKeys.find((tkf) => tkf.key === tk.key)
                        })
                        .map((ks) => ({ label: ks.title || ks.key, value: ks.key }))}
                      registration={{
                        value: k.key,
                        onChange: (e: any) => {
                          const newKey = keys.find((tk) => tk.key === e.target.value)

                          if (newKey) {
                            const cloned = [...tableKeys]

                            cloned[i] = {
                              ...cloned[i],
                              title: newKey.title || newKey.key,
                              key: newKey.key,
                              Component: newKey.Component
                            }
                            setTableKeys(cloned)
                          }
                        }
                      }}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={tbodyClasses}>
            {loading &&
              Array.from(Array(pagination?.take).keys()).map((k) => (
                <tr key={`table-data-row-skeleton-${k}`} className='border-b-2 border-surface animate-pulse text-[0.85em] min-h-[69px]'>
                  <td colSpan={100} className='bg-surface-2'>
                    <div className={classNames('opacity-0', loadingSkeletonHeight)}>Loading data...</div>
                  </td>
                </tr>
              ))}
            {!loading && modifiedData?.length === 0 && (
              <tr key='table-data-row-no-results' className='bg-surface-2 border-b-2 border-surface text-[0.85em]'>
                <td colSpan={100} className='bg-surface text-text'>
                  <div className={classNames('flex items-center', loadingSkeletonHeight)}>No results to display</div>
                </td>
              </tr>
            )}
            {!loading &&
              modifiedData?.map((d, di) => (
                <tr
                  key={`table-data-row-${di}`}
                  className={classNames(d.trClasses, 'bg-surface-2 border-b-[2px] border-surface text-[0.85em] hover:opacity-90')}
                >
                  {tableKeys.map((k, ki) => (
                    <td
                      key={`table-data-row-data-${ki}`}
                      className={classNames('text-text', di % 2 === 0 ? 'bg-surface' : 'bg-surface-2', k.tdClasses)}
                    >
                      {k.Component ? (
                        <k.Component {...d} />
                      ) : (
                        <>
                          {k.noCopy && <div>{d[k.key]}</div>}
                          {!k.noCopy && (
                            <CopyWithText
                              textValue={valueStringCheck(d[k.key])}
                              value={valueStringCheck(d[k.key])}
                              className='!ml-0 !text-[1em]'
                            />
                          )}
                        </>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            {!loading &&
              Array.from(Array((pagination?.take || 0) - (modifiedData?.length || 0)).keys()).map((k) => (
                <tr
                  key={`table-data-row-blank-${k}`}
                  className={classNames('border-b-[2px] border-surface text-[0.85em]', placeholderSkeletonHeight)}
                >
                  <td colSpan={100} className='bg-surface-2'>
                    <div className={classNames('opacity-0', placeholderSkeletonHeight)}>No data...</div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {pagination && (
        <Pagination
          loading={pagination.loading}
          data={pagination.count}
          pageSkip={pagination.skip}
          pageTake={pagination.take}
          onTakeChanged={(value) => onTakeChanged(value)}
          onSkipChanged={(value) => onSkipChanged(value)}
        />
      )}
    </>
  )
}
